// The archive screen's logic — the parts worth testing without a browser.
//
// Two of Jacky's standing rules are enforced here as code rather than left as
// good intentions: search completes as he types, and a long screen owes him a
// way back to the top.
//
// Deliberately NOT here: any blob path, folder or container name. The API
// already strips storage layout once, at the boundary (see document-archive.ts);
// this module must not reintroduce it, and a test holds that.
// Type-only: this module is pure so it can be tested without a browser, and a
// value import of the api client would drag the whole fetch seam into the test.
import type { api } from "./api";
import {isJobRef} from "@artemis-group/aiw-contracts";

export type ArchiveBrowse = Awaited<ReturnType<typeof api.archive>>;
export type ArchiveGroup = ArchiveBrowse["groups"][number];
export type ArchiveItem = ArchiveGroup["items"][number];

/** Below this the screen has not really scrolled, and offering "back to top"
 *  would be noise on a short page. */
export const BACK_TO_TOP_AFTER_PX = 600;

export function backToTopVisible(scrollY: number): boolean {
  return scrollY > BACK_TO_TOP_AFTER_PX;
}

export type SuggestionKind = "job" | "supplier" | "month" | "document" | "customer" | "order";
export interface Suggestion { value: string; kind: SuggestionKind; label: string }

/** Completions drawn from what the archive actually holds, in the order he
 *  thinks: the job, the customer on it, the supplier, the order number, the
 *  month, then the document.
 *
 *  Never a filename. A filename is something the system chose and he has never
 *  read; he looks for a document by what he remembers about the work.
 *
 *  Customer and order number are drawn only when the API supplied them. A row
 *  with no job has no customer, and a PO with no version has no number —
 *  inventing either would put a confident, wrong name in front of him. */
export function suggestions(browse: ArchiveBrowse): Suggestion[] {
  const jobs = new Map<string, Suggestion>();
  const customers = new Map<string, Suggestion>();
  const suppliers = new Map<string, Suggestion>();
  const orders = new Map<string, Suggestion>();
  const months = new Map<string, Suggestion>();
  const documents = new Map<string, Suggestion>();

  for (const group of browse.groups) {
    const groupLabel = (group.label ?? "").trim();
    if (groupLabel) {
      const bucket = group.group_by === "month_completed" ? months : suppliers;
      const kind: SuggestionKind = group.group_by === "month_completed" ? "month" : "supplier";
      if (!bucket.has(groupLabel)) bucket.set(groupLabel, { value: groupLabel, kind, label: groupLabel });
    }
    for (const item of group.items) {
      const reference = (item.reference ?? "").trim();
      if (isJobRef(reference) && !jobs.has(reference)) {
        jobs.set(reference, { value: reference, kind: "job", label: reference });
      }
      const customer = (item.customer ?? "").trim();
      if (customer && !customers.has(customer)) {
        customers.set(customer, { value: customer, kind: "customer", label: customer });
      }
      const order = (item.order_number ?? "").trim();
      if (order && !orders.has(order)) {
        orders.set(order, { value: order, kind: "order", label: order });
      }
      const title = (item.title ?? "").trim();
      if (title && !documents.has(title)) {
        documents.set(title, { value: title, kind: "document", label: title });
      }
    }
  }
  // Names read A→Z; months keep the API's newest-first order, because which
  // month he wants is almost always a recent one.
  const byLabel = (a: Suggestion, b: Suggestion) => a.label.localeCompare(b.label);
  return [
    ...[...jobs.values()].sort(byLabel),
    ...[...customers.values()].sort(byLabel),
    ...[...suppliers.values()].sort(byLabel),
    ...[...orders.values()].sort(byLabel),
    ...[...months.values()],
    ...[...documents.values()],
  ];
}

/** Completions for what has been typed so far. Prefix matches come first —
 *  typing "J07" should offer the job before anything that merely contains it. */
export function matchingSuggestions(list: Suggestion[], query: string, limit = 6): Suggestion[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const starts: Suggestion[] = [];
  const contains: Suggestion[] = [];
  for (const s of list) {
    const v = s.value.toLowerCase();
    if (v.startsWith(q)) starts.push(s);
    else if (v.includes(q)) contains.push(s);
  }
  return [...starts, ...contains].slice(0, limit);
}

function itemMatches(item: ArchiveItem, q: string): boolean {
  return [item.title, item.detail, item.reference ?? "", item.group_label, item.customer ?? "", item.order_number ?? ""]
    .some((v) => (v ?? "").toLowerCase().includes(q));
}

/** Narrow the archive to what was searched for. An empty query is not a filter
 *  — it is the whole archive, unchanged.
 *
 *  A query that matches a GROUP keeps that whole group: typing a supplier's name
 *  and being shown two of its five orders would be worse than useless. */
export function filterGroups(groups: ArchiveGroup[], query: string): ArchiveGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;
  const out: ArchiveGroup[] = [];
  for (const group of groups) {
    if ((group.label ?? "").toLowerCase().includes(q)) { out.push(group); continue; }
    const items = group.items.filter((item) => itemMatches(item, q));
    if (items.length) out.push({ ...group, items });
  }
  return out;
}

/** The newest month opens; every other month starts collapsed. Suppliers are all
 *  open — there are far fewer of them and he is scanning for one, so collapsing
 *  them would hide the thing he came for behind a click. */
export function groupStartsExpanded(group: ArchiveGroup, index: number): boolean {
  return group.group_by === "month_completed" ? index === 0 : true;
}

/** How many documents a filtered view is showing, for the count line. */
export function countItems(groups: ArchiveGroup[]): number {
  return groups.reduce((n, g) => n + g.items.length, 0);
}

/** The label the job's PO card uses for its door, taken from config so a wording
 *  change is a config edit and the card never hard-codes "Documents". */
export function jobDoorLabel(browse: ArchiveBrowse): string | null {
  return browse.entries.find((e) => e.entry_surface === "crm_job_po")?.entry_label ?? null;
}

/** Belt and braces for the one promise the archive makes about storage: the API
 *  strips the blob path at the boundary, and this is how a test proves it stayed
 *  stripped by the time it reached the face. */
export function leaksStorageLayout(value: unknown): boolean {
  return /(path|container)/i.test(JSON.stringify(value ?? null));
}
